import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppLayout } from '../../components/layout/AppLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Spinner, StateBlock } from '../../components/ui/Feedback';
import { useToast } from '../../components/ui/ToastProvider';
import { CommunicationPanel } from './components/CommunicationPanel';
import { VendorResponsesPanel } from './components/VendorResponsesPanel';
import { api, errorMessage } from '../../lib/api';
import { useAsync } from '../../lib/hooks';
import { formatDate, formatDaysLeft } from '../../lib/format';
import { mailSummaryMessage } from '../../lib/mail';
import { RFP_STATUS_META, RFP_VENDOR_STATUS_META, VENDOR_TYPE_META, rfpStatusMeta } from '../../lib/status';
import {
  RFP_VENDOR_STATUSES,
  type MailDispatchSummary,
  type Paginated,
  type ProductCategory,
  type RfpDetail,
  type RfpStatus,
  type RfpVendorStatus,
} from '../../types/api';

/**
 * RFP detail + inline edit screen. The design has no dedicated frame for it, so
 * it reuses the same panels, badges and controls as the list and wizard screens
 * while exercising the rest of the API (status changes, vendor status, edits).
 */
export function RfpDetailPage() {
  const { id } = useParams<{ id: string }>();
  const rfpId = Number(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const editing = searchParams.get('edit') === '1';

  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyVendorId, setBusyVendorId] = useState<number | null>(null);

  const loader = useCallback(() => api.get<RfpDetail>(`/rfps/${rfpId}`), [rfpId]);
  const { data: rfp, loading, error, reload } = useAsync(loader, [rfpId]);

  const categoriesLoader = useCallback(
    () => api.get<Paginated<ProductCategory>>('/product-categories', { perPage: 100 }),
    [],
  );
  const { data: categoryPage } = useAsync(categoriesLoader, []);
  const categories = categoryPage?.items ?? [];

  // Seed the edit form from the loaded record.
  useEffect(() => {
    if (!rfp) return;
    setTitle(rfp.title ?? '');
    setCategoryId(rfp.productCategoryId ?? '');
    setDueDate(rfp.dueDate ?? '');
    setNotes(rfp.notes ?? '');
  }, [rfp]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/rfps/${rfpId}`, {
        title: title || null,
        productCategoryId: categoryId === '' ? null : categoryId,
        dueDate: dueDate || null,
        notes: notes || null,
      });
      showToast('RFP updated.');
      setSearchParams({}, { replace: true });
      reload();
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status: RfpStatus) => {
    try {
      await api.post(`/rfps/${rfpId}/status`, { status });
      showToast(`Status changed to ${RFP_STATUS_META[status].label}.`);
      reload();
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    }
  };

  const sendToVendors = async () => {
    try {
      const result = await api.post<{ mail?: MailDispatchSummary }>(`/rfps/${rfpId}/send`);
      showToast(
        ['RFP sent to the selected vendors.', mailSummaryMessage(result?.mail)]
          .filter(Boolean)
          .join(' '),
      );
      reload();
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    }
  };

  const changeVendorStatus = async (vendorId: number, status: RfpVendorStatus) => {
    setBusyVendorId(vendorId);
    try {
      await api.put(`/rfps/${rfpId}/vendors/${vendorId}`, { status });
      reload();
    } catch (cause) {
      showToast(errorMessage(cause), 'error');
    } finally {
      setBusyVendorId(null);
    }
  };

  if (loading && !rfp) {
    return (
      <AppLayout title="RFP">
        <div className="state-block">
          <Spinner />
        </div>
      </AppLayout>
    );
  }

  if (error || !rfp) {
    return (
      <AppLayout title="RFP">
        <StateBlock
          title="Could not load this RFP"
          description={errorMessage(error)}
          action={
            <Button variant="outlined" onClick={() => navigate('/rfp')}>
              Back to list
            </Button>
          }
        />
      </AppLayout>
    );
  }

  const status = rfpStatusMeta(rfp.status);

  return (
    <AppLayout title="RFP">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/rfp" className="breadcrumb__link">
          RFP
        </Link>
        <span aria-hidden="true">›</span>
        <span className="breadcrumb__current">{rfp.rfpNumber}</span>
      </nav>

      <div className="detail-header">
        <div>
          <div className="detail-header__title">
            <h2 className="page-header__title">{rfp.rfpNumber}</h2>
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
          <p className="text-muted">{rfp.title ?? 'No title'}</p>
        </div>

        <div className="detail-header__actions">
          {editing ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setSearchParams({}, { replace: true })}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" onClick={() => setSearchParams({ edit: '1' })}>
                Edit
              </Button>
              <Select
                aria-label="Change status"
                wrapClassName="status-select"
                value={rfp.status}
                onChange={(event) => changeStatus(event.target.value as RfpStatus)}
                options={(Object.keys(RFP_STATUS_META) as RfpStatus[]).map((value) => ({
                  value,
                  label: RFP_STATUS_META[value].label,
                }))}
              />
              <Button variant="primary" onClick={sendToVendors}>
                Send to Vendors
              </Button>
            </>
          )}
        </div>
      </div>
      <section className="panel">
        <div className="panel__body">
          <details className="detail-collapse" open>
            <summary className="detail-section-title detail-collapse__summary">RFP Information</summary>
            {editing ? (
            <div className="review-grid">
              <label className="review-grid__label" htmlFor="detail-title">
                Title
              </label>
              <div className="review-grid__value">
                <input
                  id="detail-title"
                  className="input"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </div>

              <label className="review-grid__label" htmlFor="detail-category">
                Product Category
              </label>
              <div className="review-grid__value">
                <Select
                  id="detail-category"
                  aria-label="Product category"
                  value={categoryId === '' ? '' : categoryId}
                  onChange={(event) =>
                    setCategoryId(event.target.value === '' ? '' : Number(event.target.value))
                  }
                  options={[
                    { value: '', label: 'No category' },
                    ...categories.map((category) => ({ value: category.id, label: category.name })),
                  ]}
                  wrapClassName="review-grid__select"
                />
              </div>

              <label className="review-grid__label" htmlFor="detail-due">
                Due Date
              </label>
              <div className="review-grid__value">
                <input
                  id="detail-due"
                  type="date"
                  className="input"
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                />
              </div>

              <label className="review-grid__label" htmlFor="detail-notes">
                Notes
              </label>
              <div className="review-grid__value">
                <textarea
                  id="detail-notes"
                  className="textarea"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="detail-grid">
              <div className="detail-field">
                <p className="detail-field__label">Product Category</p>
                <p className="detail-field__value">{rfp.productCategoryName ?? '—'}</p>
              </div>
              <div className="detail-field">
                <p className="detail-field__label">Created On</p>
                <p className="detail-field__value">{formatDate(rfp.createdOn)}</p>
              </div>
              <div className="detail-field">
                <p className="detail-field__label">Due Date</p>
                <p className="detail-field__value">{formatDate(rfp.dueDate)}</p>
              </div>
              <div className="detail-field">
                <p className="detail-field__label">Days Left</p>
                <p className="detail-field__value">{formatDaysLeft(rfp.daysLeft)}</p>
              </div>
              <div className="detail-field">
                <p className="detail-field__label">Vendors</p>
                <p className="detail-field__value">
                  {rfp.vendorCount} invited · {rfp.contactedCount} contacted · {rfp.responseCount}{' '}
                  responded · {rfp.completedCount} completed
                </p>
              </div>
              <div className="detail-field">
                <p className="detail-field__label">Created By</p>
                <p className="detail-field__value">{rfp.createdBy ?? '—'}</p>
              </div>
              <div className="detail-field">
                <p className="detail-field__label">Documents</p>
                <p className="detail-field__value">
                  {rfp.documents.length > 0
                    ? rfp.documents.map((document) => document.originalName).join(', ')
                    : '—'}
                </p>
              </div>
            </div>
          )}
          </details>
        </div>
      </section>

      <section className="panel">
        <div className="panel__body">
          <details className="detail-collapse" open>
            <summary className="detail-section-title detail-collapse__summary">Products ({rfp.productLines.length})</summary>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '50%' }}>Product</th>
                  <th style={{ width: '25%' }}>Unit</th>
                  <th style={{ width: '25%' }}>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {rfp.productLines.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <StateBlock title="No products linked to this RFP" />
                    </td>
                  </tr>
                ) : null}
                {rfp.productLines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.name}</td>
                    <td>{line.unit}</td>
                    <td>{line.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </details>
        </div>
      </section>
      <section className="panel">
        <div className="panel__body">
          <details className="detail-collapse" open>
            <summary className="detail-section-title detail-collapse__summary">Vendors &amp; Status ({rfp.vendors.length})</summary>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>Vendor Name</th>
                  <th style={{ width: '12%' }}>Vendor Type</th>
                  <th style={{ width: '18%' }}>Contact Person</th>
                  <th style={{ width: '16%' }}>Contact Number</th>
                  <th style={{ width: '18%' }}>Email</th>
                  <th style={{ width: '14%' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rfp.vendors.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <StateBlock title="No vendors invited yet" />
                    </td>
                  </tr>
                ) : null}
                {rfp.vendors.map((vendor) => {
                  const typeMeta = VENDOR_TYPE_META[vendor.vendorType];
                  return (
                    <tr key={vendor.id}>
                      <td>{vendor.vendorName}</td>
                      <td>
                        <Badge variant={typeMeta.variant}>{typeMeta.label}</Badge>
                      </td>
                      <td>{vendor.contactPerson ?? '—'}</td>
                      <td>{vendor.contactNumber ?? '—'}</td>
                      <td>{vendor.email ?? '—'}</td>
                      <td>
                        <Select
                          aria-label={`Status for ${vendor.vendorName}`}
                          wrapClassName="status-select"
                          value={vendor.status}
                          disabled={busyVendorId === vendor.vendorId}
                          onChange={(event) =>
                            changeVendorStatus(vendor.vendorId, event.target.value as RfpVendorStatus)
                          }
                          options={RFP_VENDOR_STATUSES.map((value) => ({
                            value,
                            label: RFP_VENDOR_STATUS_META[value].label,
                          }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </details>
        </div>
      </section>

      <VendorResponsesPanel rfpId={rfpId} />

      <CommunicationPanel rfpId={rfpId} />

      <section className="panel">
        <div className="panel__body">
          <details className="detail-collapse" open>
            <summary className="detail-section-title detail-collapse__summary">Status History</summary>
          <div className="timeline">
            {rfp.history.length === 0 ? (
              <p className="review-empty">No status changes recorded yet.</p>
            ) : null}
            {rfp.history.map((entry) => (
              <div className="timeline__row" key={entry.id}>
                <span className="timeline__when">{entry.createdAt}</span>
                <span>
                  {entry.fromStatus ? `${entry.fromStatus} → ` : ''}
                  <strong>{entry.toStatus}</strong>
                  {entry.note ? ` — ${entry.note}` : ''}
                  {entry.changedBy ? ` (${entry.changedBy})` : ''}
                </span>
              </div>
            ))}
          </div>
          </details>
        </div>
      </section>
    </AppLayout>
  );
}
